import { UserInfo } from '@ptek/shared-auth-sdk';
import { createContext } from 'react';
import { ContestEntriesStatus } from '../api-clients/contestEntries';
import { ActiveRecorder, LastTake } from '../api-clients/sessions';
import { ContestStatus, DEFAULT_CONTEST_STATUS } from '../types/contest';
import { GeneratedDocument } from '../types/document';
import { HealthStatus } from '../types/healthStatus';
import { TranscriptLine } from '../types/recorder';
import { FormattedTemplate } from '../types/templates';
import { ContextDataItem } from '../types/widget';

export interface SessionModel {
  sessionId: string;
  transcript: TranscriptLine[];
  documents: GeneratedDocument[];
  templates: FormattedTemplate[];
  selectedTemplateId: string;
  isRecording: boolean;
  isGenerating: boolean;
  bgJobPending: boolean;
  /** BG-generation hold phase: 'finalizing' from Stop until job_id (202) arrives, then 'generating'. Absent/null in the synchronous path. */
  generationPhase?: 'finalizing' | 'generating' | null;
  appointmentId: string;
  contextData: ContextDataItem[];
  generationError: boolean;
  healthStatus: HealthStatus | null;
  transcriptDocumentId?: string;
  transcriptIsEdited?: boolean;
  transcriptUpdatedAt?: string;
  transcriptUpdatedBy?: string;
  editOverrides?: Record<
    string,
    { text: string; editedAt: string; editedBy: string }
  >;
  lastTake?: LastTake | null;
  /** A live lease owned by another browser, surfaced on session open. */
  activeRecorder?: ActiveRecorder | null;
  recordingPaused?: boolean;
}
export interface AppSharedContext {
  user?: UserInfo | null;
  isAuthenticated: boolean;
  requireAuth: boolean;
  tenantId?: string;
  getValidatedToken: () => Promise<string | null>;
  isTokenValid: () => Promise<boolean>;

  contestStatus: ContestStatus;
  contestEntries: ContestEntriesStatus;

  sessionModel: SessionModel;
  selectTemplate: (templateId: string) => Promise<void>;
  generateDocument: (options?: {
    templateId?: string;
    transcript?: string;
    clearExistingDocuments?: boolean;
  }) => Promise<GeneratedDocument | null>;
  snapshotSelectedTemplate: (
    sessionId: string,
    template?: FormattedTemplate,
    templateId?: string
  ) => Promise<void>;
  /**
   * Resolves once background-note polling is armed; the boolean is true only if
   * the note was already COMPLETED at that reconcile — a later render happens
   * asynchronously via poll/WS.
   */
  awaitBackgroundNote: (sessionId: string) => Promise<boolean>;
  updateSessionModel: (updater: (prev: SessionModel) => SessionModel) => void;
}

export const INITIAL_SESSION_MODEL: SessionModel = {
  sessionId: '',
  transcript: [],
  documents: [],
  templates: [],
  selectedTemplateId: '',
  isRecording: false,
  isGenerating: false,
  bgJobPending: false,
  appointmentId: '',
  contextData: [],
  generationError: false,
  healthStatus: null,
};

export const AppContext = createContext<AppSharedContext>({
  user: null,
  isAuthenticated: false,
  requireAuth: false,
  tenantId: undefined,
  getValidatedToken: async () => null,
  isTokenValid: async () => false,

  contestStatus: DEFAULT_CONTEST_STATUS,
  contestEntries: { entriesEarned: 0, todayEntryEarned: false },

  sessionModel: INITIAL_SESSION_MODEL,
  selectTemplate: async () => {},
  generateDocument: async () => null,
  snapshotSelectedTemplate: async () => {},
  awaitBackgroundNote: async () => false,
  updateSessionModel: () => {},
});
